import { supabase } from './supabase';
import { PostgrestFilterBuilder } from '@supabase/postgrest-js';

/**
 * Tenant-Aware Supabase Query Helpers
 * 
 * These helpers ensure ALL database queries are scoped by tenant_id
 * to prevent cross-tenant data leakage.
 * 
 * CRITICAL: Always use these helpers instead of direct supabase calls
 * after multi-tenant migration is complete.
 */

/**
 * Helper to add tenant_id filter to query builders
 * 
 * Usage:
 * ```typescript
 * const query = supabase.from('patients').select('*');
 * const tenantQuery = withTenant(query, tenantId);
 * ```
 */
export function withTenant<T>(
  query: PostgrestFilterBuilder<any, any, T>,
  tenantId: string
): PostgrestFilterBuilder<any, any, T> {
  return query.eq('tenant_id', tenantId);
}

/**
 * Helper to add tenant_id to insert/update data objects
 * 
 * Usage:
 * ```typescript
 * const patientData = { name: 'John', email: 'john@example.com' };
 * const dataWithTenant = withTenantData(patientData, tenantId);
 * // Result: { name: 'John', email: 'john@example.com', tenant_id: 'tenant-123' }
 * ```
 */
export function withTenantData<T extends Record<string, any>>(
  data: T,
  tenantId: string
): T & { tenant_id: string } {
  return {
    ...data,
    tenant_id: tenantId,
  };
}

/**
 * Helper for bulk inserts with tenant_id
 * 
 * Usage:
 * ```typescript
 * const patients = [{ name: 'John' }, { name: 'Jane' }];
 * const patientsWithTenant = withTenantDataBulk(patients, tenantId);
 * ```
 */
export function withTenantDataBulk<T extends Record<string, any>>(
  data: T[],
  tenantId: string
): (T & { tenant_id: string })[] {
  return data.map(item => withTenantData(item, tenantId));
}

/**
 * Tenant-aware select query
 * 
 * Usage:
 * ```typescript
 * const { data, error } = await selectWithTenant('patients', tenantId, '*');
 * ```
 */
export async function selectWithTenant<T = any>(
  table: string,
  tenantId: string,
  select: string = '*'
) {
  return supabase
    .from(table)
    .select(select)
    .eq('tenant_id', tenantId);
}

/**
 * Tenant-aware insert query
 * 
 * Automatically adds tenant_id to all inserted records
 * 
 * Usage:
 * ```typescript
 * const { data, error } = await insertWithTenant('patients', patientData, tenantId);
 * ```
 */
export async function insertWithTenant<T extends Record<string, any>>(
  table: string,
  data: T | T[],
  tenantId: string
) {
  const dataWithTenant = Array.isArray(data)
    ? withTenantDataBulk(data, tenantId)
    : withTenantData(data, tenantId);

  return supabase
    .from(table)
    .insert(dataWithTenant)
    .select();
}

/**
 * Tenant-aware update query
 * 
 * CRITICAL: Always includes tenant_id check to prevent cross-tenant updates
 * 
 * Usage:
 * ```typescript
 * const { data, error } = await updateWithTenant('patients', patientId, updates, tenantId);
 * ```
 */
export async function updateWithTenant<T extends Record<string, any>>(
  table: string,
  id: string,
  data: T,
  tenantId: string
) {
  return supabase
    .from(table)
    .update(data)
    .eq('id', id)
    .eq('tenant_id', tenantId)  // CRITICAL: Prevent cross-tenant updates
    .select();
}

/**
 * Tenant-aware delete query
 * 
 * CRITICAL: Always includes tenant_id check to prevent cross-tenant deletes
 * 
 * Usage:
 * ```typescript
 * const { error } = await deleteWithTenant('patients', patientId, tenantId);
 * ```
 */
export async function deleteWithTenant(
  table: string,
  id: string,
  tenantId: string
) {
  return supabase
    .from(table)
    .delete()
    .eq('id', id)
    .eq('tenant_id', tenantId);  // CRITICAL: Prevent cross-tenant deletes
}

/**
 * Tenant-aware upsert query
 * 
 * Usage:
 * ```typescript
 * const { data, error } = await upsertWithTenant('patients', patientData, tenantId);
 * ```
 */
export async function upsertWithTenant<T extends Record<string, any>>(
  table: string,
  data: T | T[],
  tenantId: string,
  options?: { onConflict?: string }
) {
  const dataWithTenant = Array.isArray(data)
    ? withTenantDataBulk(data, tenantId)
    : withTenantData(data, tenantId);

  return supabase
    .from(table)
    .upsert(dataWithTenant, options)
    .select();
}

/**
 * Tenant-aware count query
 * 
 * Usage:
 * ```typescript
 * const { count, error } = await countWithTenant('patients', tenantId);
 * ```
 */
export async function countWithTenant(
  table: string,
  tenantId: string,
  filters?: Record<string, any>
) {
  let query = supabase
    .from(table)
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', tenantId);

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  return query;
}

/**
 * Tenant-aware single record fetch
 * 
 * Usage:
 * ```typescript
 * const { data, error } = await getSingleWithTenant('patients', patientId, tenantId);
 * ```
 */
export async function getSingleWithTenant<T = any>(
  table: string,
  id: string,
  tenantId: string,
  select: string = '*'
) {
  return supabase
    .from(table)
    .select(select)
    .eq('id', id)
    .eq('tenant_id', tenantId)
    .single();
}

/**
 * Tenant-aware query with custom filters
 * 
 * Usage:
 * ```typescript
 * const { data, error } = await queryWithTenant('patients', tenantId, (query) => 
 *   query.eq('status', 'active').order('created_at', { ascending: false })
 * );
 * ```
 */
export async function queryWithTenant<T = any>(
  table: string,
  tenantId: string,
  queryBuilder: (query: any) => any,
  select: string = '*'
) {
  const baseQuery = supabase
    .from(table)
    .select(select)
    .eq('tenant_id', tenantId);

  return queryBuilder(baseQuery);
}

/**
 * Type guard to check if data has tenant_id
 */
export function hasTenantId(data: any): data is { tenant_id: string } {
  return data && typeof data === 'object' && 'tenant_id' in data;
}

/**
 * Validate that data has correct tenant_id
 * 
 * Throws error if tenant_id is missing or doesn't match
 */
export function validateTenantId(data: any, expectedTenantId: string): void {
  if (!hasTenantId(data)) {
    throw new Error('Data is missing tenant_id');
  }
  if (data.tenant_id !== expectedTenantId) {
    throw new Error(`Tenant ID mismatch: expected ${expectedTenantId}, got ${data.tenant_id}`);
  }
}
